import { useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { adminQueries, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { announcementSchema, toDateTimeLocal } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  CheckboxField,
  ErrorState,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRootError,
  HtmlEditor,
  InputField,
  PageHeader,
  SearchInput,
  toast,
} from '@blimo/ui';
import { DetailSkeleton } from '../../../components/data.jsx';
import { usePropertyOptions } from '../../../components/property-options.js';

const FIELDS = ['title', 'body_html', 'property_ids', 'is_published', 'publish_at', 'unpublish_at'];

const breadcrumb = (
  <Link to="/kommunikation/mitteilungen" className="hover:underline">
    Mitteilungen
  </Link>
);

export function AnnouncementCreatePage() {
  const search = useSearch({ strict: false });
  return (
    <>
      <PageHeader breadcrumb={breadcrumb} title="Mitteilung erstellen" description="Anhänge können nach dem Speichern hinzugefügt werden." />
      <AnnouncementForm announcement={null} defaultPropertyId={search.property} />
    </>
  );
}

export function AnnouncementEditPage() {
  const api = useApi();
  const { announcementId } = useParams({ strict: false });
  const { data, error, isPending, refetch } = useQuery(adminQueries.announcement(api, announcementId));
  if (error) {
    return (
      <>
        <PageHeader breadcrumb={breadcrumb} title="Mitteilung bearbeiten" />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Mitteilung nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;
  return (
    <>
      <PageHeader breadcrumb={breadcrumb} title="Mitteilung bearbeiten" description={data.title} />
      <AnnouncementForm announcement={data} />
    </>
  );
}

/** @param {{ announcement: any | null }} props */
function AnnouncementForm({ announcement, defaultPropertyId }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const create = !announcement;

  const form = useForm({
    resolver: zodResolver(announcementSchema),
    defaultValues: {
      title: announcement?.title ?? '',
      body_html: announcement?.body_html ?? '',
      property_ids: announcement ? announcement.properties.map((p) => String(p.id)) : defaultPropertyId ? [String(defaultPropertyId)] : [],
      is_published: announcement?.is_published ?? false,
      publish_at: toDateTimeLocal(announcement?.publish_at),
      unpublish_at: toDateTimeLocal(announcement?.unpublish_at),
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => (create ? api.admin.announcements.create(payload) : api.admin.announcements.update(announcement.id, payload)),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('announcements') });
      toast.success(create ? 'Mitteilung erstellt' : 'Mitteilung gespeichert');
      navigate({ to: '/kommunikation/mitteilungen/$announcementId', params: { announcementId: String(saved.id) } });
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const payload = {
      title: values.title.trim(),
      body_html: values.body_html || null,
      property_ids: values.property_ids.map(Number),
      is_published: values.is_published,
      publish_at: values.publish_at || null,
      unpublish_at: values.unpublish_at || null,
    };
    try {
      await mutation.mutateAsync(payload);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: FIELDS });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="flex min-w-0 flex-col gap-4">
          <FormRootError />
          <InputField name="title" label="Titel" autoComplete="off" />
          <FormField
            name="body_html"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Text</FormLabel>
                <FormControl>
                  <HtmlEditor label="Text" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                </FormControl>
                <FormDescription>
                  HTML mit einfacher Formatierung. Der Text wird beim Speichern serverseitig bereinigt (keine Skripte, keine Formulare).
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle as="h2">Veröffentlichung</CardTitle>
              <CardDescription>Sichtbar im Portal, solange veröffentlicht und im Zeitraum.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <CheckboxField name="is_published" label="Veröffentlicht" />
              <InputField name="publish_at" label="Sichtbar ab" type="datetime-local" description="Leer = sofort" />
              <InputField name="unpublish_at" label="Sichtbar bis" type="datetime-local" description="Leer = unbegrenzt" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle as="h2">Liegenschaften</CardTitle>
              <CardDescription>Die Mitteilung erscheint bei den Eigentümern dieser Liegenschaften.</CardDescription>
            </CardHeader>
            <CardContent>
              <PropertyMultiSelect selectedProperties={announcement?.properties ?? []} />
            </CardContent>
          </Card>
          <div className="flex gap-2">
            <Button type="submit" loading={form.formState.isSubmitting}>
              {create ? 'Erstellen' : 'Speichern'}
            </Button>
            <Button asChild variant="outline">
              {create ? (
                <Link to="/kommunikation/mitteilungen">Abbrechen</Link>
              ) : (
                <Link to="/kommunikation/mitteilungen/$announcementId" params={{ announcementId: String(announcement.id) }}>
                  Abbrechen
                </Link>
              )}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

/**
 * Checkbox list of properties (all non-archived from GET /admin/lookup/properties) bound to `property_ids`.
 * @param {{ selectedProperties: Array<{ id: number, name?: string|null, number?: string|null }> }} props
 */
function PropertyMultiSelect({ selectedProperties }) {
  const { control } = useFormContext();
  const [q, setQ] = useState('');
  const { options: lookup, isPending } = usePropertyOptions();
  const options = useMemo(() => {
    const list = lookup.map((o) => ({ id: o.value, label: o.label }));
    for (const p of selectedProperties) {
      if (!list.some((o) => o.id === String(p.id))) list.unshift({ id: String(p.id), label: p.name || `Liegenschaft #${p.id}` });
    }
    return list;
  }, [lookup, selectedProperties]);
  const visible = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;

  return (
    <FormField
      name="property_ids"
      control={control}
      render={({ field }) => {
        const selected = new Set(field.value ?? []);
        const toggle = (id) => field.onChange(selected.has(id) ? field.value.filter((v) => v !== id) : [...(field.value ?? []), id]);
        return (
          <FormItem>
            <FormLabel className="sr-only">Liegenschaften</FormLabel>
            <SearchInput
              label="Liegenschaften filtern"
              placeholder="Liegenschaft suchen …"
              value={q}
              onValueChange={setQ}
              delay={0}
              wrapperClassName="sm:max-w-none"
            />
            <FormControl>
              <fieldset aria-label="Liegenschaften" className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border p-2">
                {isPending && <p className="text-sm text-muted-foreground">Wird geladen …</p>}
                {!isPending && visible.length === 0 && <p className="text-sm text-muted-foreground">Keine Liegenschaft gefunden.</p>}
                {visible.map((o) => (
                  <label key={o.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
                    {o.label}
                  </label>
                ))}
              </fieldset>
            </FormControl>
            <FormDescription>{selected.size} ausgewählt</FormDescription>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
